import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { 
  FileText, 
  CheckSquare, 
  DollarSign, 
  Clock, 
  Edit, 
  Copy, 
  Archive,
  Settings
} from "lucide-react";

interface MobileActionsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEditJob: () => void;
  onDuplicateJob: () => void;
  onArchiveJob: () => void;
  onOpenDocuments: () => void;
  onOpenTasks: () => void;
  onOpenPayments: () => void;
  onOpenTimeline: () => void;
  onStatusChange: (status: string) => void;
  currentStatus: string;
}

export const MobileActionsSheet = ({
  open,
  onOpenChange,
  onEditJob,
  onDuplicateJob,
  onArchiveJob,
  onOpenDocuments,
  onOpenTasks,
  onOpenPayments,
  onOpenTimeline,
  onStatusChange,
  currentStatus,
}: MobileActionsSheetProps) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85vh]">
        <SheetHeader>
          <SheetTitle>Job Actions</SheetTitle>
        </SheetHeader>
        
        <div className="mt-6 space-y-6">
          {/* Quick Actions */}
          <div>
            <h3 className="text-sm font-medium mb-3">Quick Access</h3>
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="h-20 flex-col gap-2"
                onClick={() => {
                  onOpenDocuments();
                  onOpenChange(false);
                }}
              >
                <FileText className="h-5 w-5" />
                <span className="text-xs">Documents</span>
              </Button>
              <Button
                variant="outline"
                className="h-20 flex-col gap-2"
                onClick={() => {
                  onOpenTasks();
                  onOpenChange(false);
                }}
              >
                <CheckSquare className="h-5 w-5" />
                <span className="text-xs">Tasks</span>
              </Button>
              <Button
                variant="outline"
                className="h-20 flex-col gap-2"
                onClick={() => {
                  onOpenPayments();
                  onOpenChange(false);
                }}
              >
                <DollarSign className="h-5 w-5" />
                <span className="text-xs">Payments</span>
              </Button>
              <Button
                variant="outline"
                className="h-20 flex-col gap-2"
                onClick={() => {
                  onOpenTimeline();
                  onOpenChange(false);
                }}
              >
                <Clock className="h-5 w-5" />
                <span className="text-xs">Timeline</span>
              </Button>
            </div>
          </div>

          {/* Status Change */}
          <div>
            <h3 className="text-sm font-medium mb-3">Change Status</h3>
            <div className="grid grid-cols-2 gap-2">
              {["new", "contacted", "scheduled", "in_progress", "proposed", "completed"].map((status) => (
                <Button
                  key={status}
                  variant={currentStatus === status ? "default" : "outline"}
                  size="sm"
                  className="justify-start"
                  onClick={() => {
                    onStatusChange(status);
                    onOpenChange(false);
                  }}
                >
                  {status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                </Button>
              ))}
            </div>
          </div>

          {/* Job Actions */}
          <div>
            <h3 className="text-sm font-medium mb-3">Job Actions</h3>
            <div className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start gap-3"
                onClick={() => {
                  onEditJob();
                  onOpenChange(false);
                }}
              >
                <Edit className="h-4 w-4" />
                Edit Job Details
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start gap-3"
                onClick={() => {
                  onDuplicateJob();
                  onOpenChange(false);
                }}
              >
                <Copy className="h-4 w-4" />
                Duplicate Job
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start gap-3 text-destructive"
                onClick={() => {
                  onArchiveJob();
                  onOpenChange(false);
                }}
              >
                <Archive className="h-4 w-4" />
                Archive Job
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
